import { api } from "@/api/client";

/** The cards asked for in the same moment travel in one call.
 *
 *  A list of projects used to ask for each card on its own, one call per card,
 *  and the server has only a few workers to answer them with. Each card still
 *  asks for itself, so its cache and its refresh are unchanged; the calls made
 *  within a few milliseconds of each other are merged here.
 */

const WINDOW_MS = 12;
const BATCH = 100;

type Waiter = { resolve(value: { data: Record<string, unknown> }): void; reject(error: unknown): void };

let pending = new Map<number, Waiter[]>();
let timer: number | null = null;

async function flush() {
    timer = null;
    const batch = pending;
    pending = new Map();
    const ids = [...batch.keys()];
    for (let start = 0; start < ids.length; start += BATCH) {
        const chunk = ids.slice(start, start + BATCH);
        try {
            const answer = await api.raw<Record<string, Record<string, unknown>>>(
                `/projects/cards?ids=${chunk.join(",")}`);
            for (const id of chunk) {
                const card = answer.data[String(id)] ?? {};
                for (const waiter of batch.get(id) ?? []) {
                    waiter.resolve({ data: card });
                }
            }
        } catch (error) {
            for (const id of chunk) {
                for (const waiter of batch.get(id) ?? []) {
                    waiter.reject(error);
                }
            }
        }
    }
}

export function loadProjectCard<T>(id: number): Promise<{ data: T }> {
    return new Promise((resolve, reject) => {
        const waiters = pending.get(id) ?? [];
        waiters.push({ resolve: resolve as Waiter["resolve"], reject });
        pending.set(id, waiters);
        if (timer === null) {
            timer = window.setTimeout(() => void flush(), WINDOW_MS);
        }
    });
}
